/* Job Change Trigger — champion watchlist page.
 *
 * Watch CHRO/HR-leadership contacts on LinkedIn. A daily capped sweep
 * (Zevari MCP, read-only) compares each live profile against last-known
 * state; confirmed ICP-qualified changes become leadership_change signals
 * in Signal Hunting. The report arrives as a message from the Master
 * Recruiter agent.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { LinkedInButton } from "@/components/common/LinkedInButton";
import ContactDrawer from "@/components/ContactDrawer";
import {
    ArrowLeftRight, Loader2, Plus, Trash2, Play, Pause, RefreshCw,
    Linkedin, Sparkles, Users, Radar, ShieldCheck, AlertTriangle,
    CheckCircle2, X, ExternalLink,
} from "lucide-react";
import { fmtDateTime } from "@/lib/datetime";

const ZEV = "#38bdf8";

function StatCard({ label, value, testid }) {
    return (
        <div className="rounded-xl border border-white/8 bg-[var(--jr-surface)] px-4 py-3" data-testid={testid}>
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">{label}</div>
            <div className="text-xl font-semibold text-white mt-0.5">{value}</div>
        </div>
    );
}

function AddContactForm({ onAdded, onClose }) {
    const { t } = useTranslation();
    const [form, setForm] = useState({ name: "", linkedin_url: "", role: "", company: "" });
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        setBusy(true);
        try {
            await api.post("/job-changes/watchlist", form);
            toast.success(t("jobchange.added_toast"));
            onAdded();
            onClose();
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("jobchange.failed"));
        } finally { setBusy(false); }
    };
    const inp = "w-full rounded-lg border border-white/10 bg-[var(--jr-surface-2)] px-3 py-2 text-sm text-white outline-none focus:border-[#38bdf8]/50";
    return (
        <div className="rounded-xl border border-white/10 bg-[var(--jr-surface)] p-4 space-y-3" data-testid="jobchange-add-form">
            <div className="flex items-center justify-between">
                <div className="text-sm font-medium text-white">{t("jobchange.add_title")}</div>
                <button onClick={onClose} className="p-1 rounded text-[var(--jr-text-3)] hover:text-white"><X size={14} /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
                <input className={inp} placeholder={t("jobchange.f_name")} value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="jobchange-add-name" />
                <input className={inp} placeholder="https://linkedin.com/in/…" value={form.linkedin_url}
                    onChange={(e) => setForm({ ...form, linkedin_url: e.target.value })} data-testid="jobchange-add-url" />
                <input className={inp} placeholder={t("jobchange.f_role")} value={form.role}
                    onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="jobchange-add-role" />
                <input className={inp} placeholder={t("jobchange.f_company")} value={form.company}
                    onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="jobchange-add-company" />
            </div>
            <button onClick={submit} disabled={busy || !form.name.trim() || !form.linkedin_url.trim()}
                data-testid="jobchange-add-submit"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold text-black disabled:opacity-40"
                style={{ background: ZEV }}>
                {busy ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} {t("jobchange.add_btn")}
            </button>
        </div>
    );
}

function SweepReport({ run }) {
    const { t } = useTranslation();
    if (!run) return null;
    return (
        <div className="rounded-xl border border-white/8 bg-[var(--jr-surface)] p-4" data-testid="jobchange-report">
            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">
                <Radar size={11} style={{ color: ZEV }} />
                {t("jobchange.report_title")} · {fmtDateTime(run.ran_at)} · {run.triggered_by}
            </div>
            <div className="mt-2 flex items-center gap-4 text-xs text-[var(--jr-text-2)] flex-wrap">
                <span>{t("jobchange.r_checked", { n: run.checked })}</span>
                <span className="text-[var(--jr-success)]">{t("jobchange.r_changes", { n: (run.changes || []).length })}</span>
                <span>{t("jobchange.r_signals", { n: run.signals_created })}</span>
                {run.icp_failed?.length > 0 && <span className="text-[var(--jr-amber)]">{t("jobchange.r_icp_failed", { n: run.icp_failed.length })}</span>}
                {run.lookup_failed?.length > 0 && <span className="text-[var(--jr-text-3)]">{t("jobchange.r_lookup_failed", { n: run.lookup_failed.length })}</span>}
                {run.baselined > 0 && <span className="text-[var(--jr-text-3)]">{t("jobchange.r_baselined", { n: run.baselined })}</span>}
            </div>
            {(run.changes || []).length > 0 && (
                <div className="mt-3 space-y-1.5">
                    {run.changes.map((c, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs rounded-lg border border-[var(--jr-success)]/20 bg-[var(--jr-success)]/[0.05] px-3 py-2"
                            data-testid={`jobchange-change-${i}`}>
                            <CheckCircle2 size={12} className="text-[var(--jr-success)] shrink-0" />
                            <span className="text-white font-medium">{c.name}</span>
                            <span className="text-[var(--jr-text-3)]">{c.old || "?"}</span>
                            <ArrowLeftRight size={10} className="text-[var(--jr-text-3)] shrink-0" />
                            <span className="text-[var(--jr-text-2)]">{c.new}</span>
                            {c.icp_score != null && <span className="font-mono text-[10px] text-[var(--jr-text-3)]">ICP {c.icp_score}</span>}
                            <Link to="/app/sales/signals" className="ml-auto text-[10px] hover:underline" style={{ color: ZEV }}>
                                {t("jobchange.view_signal")}
                            </Link>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function JobChanges() {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showAdd, setShowAdd] = useState(false);
    const [busySeed, setBusySeed] = useState(false);
    const [busySweep, setBusySweep] = useState(false);
    const [sweepResult, setSweepResult] = useState(null);
    const [selected, setSelected] = useState(new Set());
    const [bulkBusy, setBulkBusy] = useState(false);
    const [drawerRef, setDrawerRef] = useState(null);

    const load = useCallback(async () => {
        try {
            const r = await api.get("/job-changes/watchlist");
            setData(r.data);
        } catch (e) { /* silent */ }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const seed = async () => {
        setBusySeed(true);
        try {
            const r = await api.post("/job-changes/watchlist/seed");
            toast.success(t("jobchange.seed_toast", { n: r.data.added }));
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || t("jobchange.failed")); }
        finally { setBusySeed(false); }
    };

    const runSweep = async () => {
        setBusySweep(true);
        try {
            const r = await api.post("/job-changes/sweep/run", {}, { timeout: 300000 });
            setSweepResult(r.data);
            const n = (r.data.changes || []).length;
            toast.success(n ? t("jobchange.sweep_changes_toast", { n }) : t("jobchange.sweep_done_toast"));
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || t("jobchange.failed")); }
        finally { setBusySweep(false); }
    };

    const toggle = async (id) => {
        try { await api.patch(`/job-changes/watchlist/${id}/toggle`); load(); }
        catch (e) { toast.error(t("jobchange.failed")); }
    };
    const remove = async (id) => {
        try { await api.delete(`/job-changes/watchlist/${id}`); load(); }
        catch (e) { toast.error(t("jobchange.failed")); }
    };

    const contacts = data?.contacts || [];

    const toggleSelect = (id) => {
        setSelected((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };
    const allSelected = contacts.length > 0 && contacts.every((c) => selected.has(c.id));
    const toggleSelectAll = () => {
        setSelected(allSelected ? new Set() : new Set(contacts.map((c) => c.id)));
    };
    const bulkDelete = async () => {
        if (!window.confirm(t("jobchange.bulk_delete_confirm", { n: selected.size }))) return;
        setBulkBusy(true);
        try {
            const r = await api.post("/job-changes/watchlist/bulk-delete", { ids: [...selected] });
            toast.success(t("jobchange.bulk_deleted_toast", { n: r.data.deleted }));
            setSelected(new Set());
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || t("jobchange.failed")); }
        finally { setBulkBusy(false); }
    };

    const stats = data?.stats || {};
    const report = sweepResult || data?.last_run;

    return (
        <div className="p-6 lg:p-8 space-y-5 max-w-6xl" data-testid="jobchanges-page">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="font-display text-2xl text-white flex items-center gap-2.5">
                        <ArrowLeftRight size={22} style={{ color: ZEV }} /> {t("jobchange.title")}
                    </h1>
                    <p className="text-sm text-[var(--jr-text-2)] mt-1 max-w-2xl">{t("jobchange.subtitle")}</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <button onClick={seed} disabled={busySeed} data-testid="jobchange-seed-btn"
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-medium border border-white/10 text-[var(--jr-text-2)] hover:text-white hover:bg-white/5 disabled:opacity-50">
                        {busySeed ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} {t("jobchange.seed_btn")}
                    </button>
                    <button onClick={() => setShowAdd(true)} data-testid="jobchange-add-open"
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-medium border border-white/10 text-[var(--jr-text-2)] hover:text-white hover:bg-white/5">
                        <Plus size={12} /> {t("jobchange.add_btn")}
                    </button>
                    <button onClick={runSweep} disabled={busySweep || !contacts.length} data-testid="jobchange-sweep-btn"
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold text-black disabled:opacity-40"
                        style={{ background: ZEV }}>
                        {busySweep ? <Loader2 size={12} className="animate-spin" /> : <Radar size={12} />}
                        {busySweep ? t("jobchange.sweeping") : t("jobchange.sweep_btn")}
                    </button>
                </div>
            </div>

            <div className="rounded-lg border border-white/8 bg-[var(--jr-surface-2)]/40 px-3 py-2.5 flex items-start gap-2 max-w-3xl">
                <ShieldCheck size={13} className="mt-0.5 shrink-0" style={{ color: ZEV }} />
                <p className="text-[11px] text-[var(--jr-text-3)] leading-relaxed">{t("jobchange.explain")}</p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <StatCard label={t("jobchange.s_watchlist")} value={stats.total ?? "—"} testid="jobchange-stat-total" />
                <StatCard label={t("jobchange.s_active")} value={stats.active ?? "—"} testid="jobchange-stat-active" />
                <StatCard label={t("jobchange.s_changes")} value={stats.total_changes ?? "—"} testid="jobchange-stat-changes" />
                <StatCard label={t("jobchange.s_last_run")}
                    value={stats.last_run_at ? fmtDateTime(stats.last_run_at) : "—"}
                    testid="jobchange-stat-lastrun" />
            </div>

            {showAdd && <AddContactForm onAdded={load} onClose={() => setShowAdd(false)} />}

            <SweepReport run={report} />

            {loading ? (
                <div className="py-16 grid place-items-center text-[var(--jr-text-3)]"><Loader2 className="animate-spin" size={20} /></div>
            ) : contacts.length === 0 ? (
                <div className="rounded-xl border border-white/8 bg-[var(--jr-surface)] p-8 text-center" data-testid="jobchange-empty">
                    <Users size={24} className="mx-auto text-[var(--jr-text-3)]" />
                    <p className="text-sm text-[var(--jr-text-2)] mt-2">{t("jobchange.empty")}</p>
                </div>
            ) : (
                <div className="rounded-xl border border-white/8 bg-[var(--jr-surface)] overflow-hidden">
                    {selected.size > 0 && (
                        <div className="flex items-center gap-3 px-4 py-2.5 border-b border-[var(--jr-brand)]/25 bg-[var(--jr-brand)]/[0.07]"
                            data-testid="jobchange-bulk-bar">
                            <span className="text-xs text-[var(--jr-text-2)]">
                                {t("jobchange.selected_n", { n: selected.size })}
                            </span>
                            <button onClick={bulkDelete} disabled={bulkBusy} data-testid="jobchange-bulk-delete"
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border border-[#ef4444]/40 bg-[#ef4444]/10 text-[#ef4444] hover:bg-[#ef4444]/20 disabled:opacity-50">
                                {bulkBusy ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                                {t("jobchange.bulk_delete", { n: selected.size })}
                            </button>
                            <button onClick={() => setSelected(new Set())} data-testid="jobchange-bulk-clear"
                                className="ml-auto text-xs text-[var(--jr-text-3)] hover:text-white">
                                {t("common.cancel")}
                            </button>
                        </div>
                    )}
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)] border-b border-white/8">
                                <th className="pl-4 pr-1 py-2.5 w-8">
                                    <button onClick={toggleSelectAll} data-testid="jobchange-select-all"
                                        aria-label={t("jobchange.select_all")}
                                        title={t("jobchange.select_all")}
                                        className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${allSelected ? "bg-[var(--jr-brand)] border-[var(--jr-brand)]" : "border-white/25 hover:border-white/50"}`}>
                                        {allSelected && <svg width="9" height="9" viewBox="0 0 12 12" fill="none"><path d="M2.5 6L5 8.5L9.5 3.5" stroke="#000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                                    </button>
                                </th>
                                <th className="px-4 py-2.5">{t("jobchange.c_name")}</th>
                                <th className="px-4 py-2.5">{t("jobchange.c_role")}</th>
                                <th className="px-4 py-2.5">{t("jobchange.c_company")}</th>
                                <th className="px-4 py-2.5">{t("jobchange.c_checked")}</th>
                                <th className="px-4 py-2.5">{t("jobchange.c_changes")}</th>
                                <th className="px-4 py-2.5"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {contacts.map((c) => (
                                <tr key={c.id} className={`border-b border-white/5 hover:bg-white/[0.02] ${selected.has(c.id) ? "bg-[var(--jr-brand)]/[0.05]" : ""}`} data-testid={`jobchange-row-${c.id}`}>
                                    <td className="pl-4 pr-1 py-2.5 w-8">
                                        <button onClick={() => toggleSelect(c.id)} data-testid={`jobchange-select-${c.id}`}
                                            aria-label={t("jobchange.select_one")}
                                            className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${selected.has(c.id) ? "bg-[var(--jr-brand)] border-[var(--jr-brand)]" : "border-white/25 hover:border-white/50"}`}>
                                            {selected.has(c.id) && <svg width="9" height="9" viewBox="0 0 12 12" fill="none"><path d="M2.5 6L5 8.5L9.5 3.5" stroke="#000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                                        </button>
                                    </td>
                                    <td className="px-4 py-2.5">
                                        <div className="flex items-center gap-2">
                                            <LinkedInButton url={c.linkedin_url} name={c.name} variant="icon" className="!w-5 !h-5" testid={`jobchange-linkedin-${c.id}`} />
                                            <span className={`cursor-pointer hover:text-[var(--jr-brand)] hover:underline underline-offset-2 transition-colors ${c.status === "paused" ? "text-[var(--jr-text-3)]" : "text-white"}`}
                                                onClick={() => setDrawerRef({ kind: "watchlist", contactId: c.id })}
                                                data-testid={`open-contact-${c.id}`}>{c.name}</span>
                                            {c.source === "auto" && (
                                                <span className="text-[8px] font-mono uppercase tracking-[0.14em] px-1.5 py-0.5 rounded bg-white/5 text-[var(--jr-text-3)]">auto</span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-4 py-2.5 text-[var(--jr-text-2)] text-xs">{c.role || "—"}</td>
                                    <td className="px-4 py-2.5 text-[var(--jr-text-2)] text-xs">{c.company || "—"}</td>
                                    <td className="px-4 py-2.5 text-[var(--jr-text-3)] text-xs font-mono">
                                        {c.last_checked_at ? fmtDateTime(c.last_checked_at) : "—"}
                                    </td>
                                    <td className="px-4 py-2.5">
                                        {(c.change_history || []).length > 0 ? (
                                            <span className="text-xs text-[var(--jr-success)] font-medium">{c.change_history.length}</span>
                                        ) : <span className="text-xs text-[var(--jr-text-3)]">0</span>}
                                    </td>
                                    <td className="px-4 py-2.5">
                                        <div className="flex items-center gap-1 justify-end">
                                            <button onClick={() => toggle(c.id)} title={c.status === "active" ? t("jobchange.pause") : t("jobchange.resume")}
                                                data-testid={`jobchange-toggle-${c.id}`}
                                                className="p-1.5 rounded hover:bg-white/5 text-[var(--jr-text-3)] hover:text-white">
                                                {c.status === "active" ? <Pause size={12} /> : <Play size={12} />}
                                            </button>
                                            <button onClick={() => remove(c.id)} data-testid={`jobchange-delete-${c.id}`}
                                                className="p-1.5 rounded hover:bg-[#ef4444]/10 text-[var(--jr-text-3)] hover:text-[#ef4444]">
                                                <Trash2 size={12} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            <ContactDrawer contactRef={drawerRef} onClose={() => setDrawerRef(null)} onChanged={load} />
        </div>
    );
}
